// Shared audit-metadata helpers so every document records who created/updated/
// completed it and when, in a consistent shape. These power the reporting
// enablers (productivity, throughput, resolution time, spend, etc.).
//
// Standard fields:
//   createdAt / createdBy / createdByUid
//   updatedAt / updatedBy / updatedByUid
//   completedAt / completedBy / completedByUid
//
// Timestamps use Firestore serverTimestamp() so they're server-authoritative
// and comparable across clients/timezones.
import { serverTimestamp } from "firebase/firestore";
import { auth } from "../firebase";

/**
 * Resolve a display name + uid for the acting user.
 * Prefers a rich `user`/`userInfo` object (has fullName); falls back to the
 * signed-in Firebase auth user (uid + email) when none is passed.
 * @param {object} [user]
 * @returns {{ name: string, uid: string|null }}
 */
export const actorFrom = (user) => {
  const u = user || auth.currentUser || {};
  const name =
    u.fullName || u.displayName || u.personalEmail || u.email || "Unknown";
  const uid = u.id || u.uid || null;
  return { name, uid };
};

/** Metadata for a newly created document. */
export const createMeta = (user) => {
  const { name, uid } = actorFrom(user);
  return { createdAt: serverTimestamp(), createdBy: name, createdByUid: uid };
};

/** Metadata for an update to an existing document. */
export const updateMeta = (user) => {
  const { name, uid } = actorFrom(user);
  return { updatedAt: serverTimestamp(), updatedBy: name, updatedByUid: uid };
};

/** Metadata for marking a document complete/closed. */
export const completeMeta = (user) => {
  const { name, uid } = actorFrom(user);
  return {
    completedAt: serverTimestamp(),
    completedBy: name,
    completedByUid: uid,
  };
};
